// state/mode/dataModeSlice.js
import { createSlice } from "@reduxjs/toolkit";

const LS_KEY = "dataMode.selected";

// safe window access
const getEnv = () => {
  try { return (typeof window !== "undefined" && window._env_) || {}; }
  catch { return {}; }
};

const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v.toLowerCase() === "true";
  return !!v;
};

const computeAvailability = (env = getEnv()) => {
  const r = env.runtimeTenantConfig || {};
  const raw = toBool(r.tenantHasRawDataRepresentation);
  const processed = toBool(r.tenantHasProcessedDataRepresentation);
  return { raw, processed };
};

const computeDefaultMode = (available) => {
  // your rule: if both enabled → RAW; if only one → that one; if none → fallback to "processed"
  if (available.raw && available.processed) return "raw";
  if (available.raw) return "raw";
  if (available.processed) return "processed";
  return "processed"; // fallback
};

const coerceMode = (requested, available) => {
  if (requested === "raw" && available.raw) return "raw";
  if (requested === "processed" && available.processed) return "processed";
  return computeDefaultMode(available);
};

const readPersisted = () => {
  try { return localStorage.getItem(LS_KEY) || null; } catch { return null; }
};

const initialFromEnv = () => {
  const available = computeAvailability();
  const persisted = readPersisted();
  const mode = coerceMode(persisted, available);
  const canToggle = available.raw && available.processed;
  return { mode, available, canToggle };
};

const dataModeSlice = createSlice({
  name: "dataMode",
  initialState: initialFromEnv(),
  reducers: {
    initFromEnv(state) {
      const available = computeAvailability();
      state.available = available;
      state.mode = coerceMode(state.mode, available); // keep current if still valid
      state.canToggle = available.raw && available.processed;
    },
    setMode(state, action) {
      const requested = action.payload === "processed" ? "processed" : "raw";
      state.mode = coerceMode(requested, state.available);
      try { localStorage.setItem(LS_KEY, state.mode); } catch {}
    },
    toggleMode(state) {
      const next = state.mode === "raw" ? "processed" : "raw";
      state.mode = coerceMode(next, state.available);
      try { localStorage.setItem(LS_KEY, state.mode); } catch {}
    }
  }
});

export const { initFromEnv, setMode, toggleMode } = dataModeSlice.actions;
export const selectDataMode = (state) => (state.dataModeSlice?.mode ?? "raw");
export const selectDataModeMeta = (state) => state.dataModeSlice || initialFromEnv();
export default dataModeSlice.reducer;
